import { FC, useEffect, useRef } from 'react';

import { usePage } from '@inertiajs/react';
import { Filter } from 'lucide-react';

import { Button } from '../../../../components/ui/Button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/Select';
import { Expansion, ProductFilter, ProductType } from '../../../../types';
import { PropertyOption } from '../../../../types/shared';
import { cn } from '../../../../utils';
import { expansionFilterOptions } from '../../../Products/filters/expansionFilterOptions';
import { PropertyFilterSelect } from '../../../Products/filters/PropertyFilterSelect';

type PageProps = {
    currentProductType: ProductType | null;
    filtersConfig: Record<string, ProductFilter[]>;
    expansions: Expansion[] | null;
};

interface Props {
    readonly filters: Record<string, string>;
    readonly onFilterChange: (filters: {
        q: Record<string, string>;
        expansionSlug?: string;
        preserveState?: boolean;
    }) => void;
    readonly onResetFilters: () => void;
    readonly expansions: Expansion[] | null;
    readonly expansion: Expansion | null;
    readonly extraFilters?: {
        key: string;
        label: string;
        allLabel: string;
        options: PropertyOption[];
        searchable?: boolean;
        multiselect?: boolean;
        testId?: string;
    }[];
    readonly hidePropertyFiltersWithoutProductType?: boolean;
    readonly sticky?: boolean;
    readonly showBorder?: boolean;
    readonly sort?: string;
    readonly onSortChange?: (value: string) => void;
    readonly className?: string;
}

export const ProfileDesktopFilters: FC<Props> = ({
    filters,
    onFilterChange,
    onResetFilters,
    expansion,
    expansions,
    extraFilters,
    hidePropertyFiltersWithoutProductType = false,
    sticky = true,
    showBorder = true,
    sort,
    onSortChange,
    className,
}) => {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const filtersRef = useRef<HTMLDivElement>(null);
    const { currentProductType, filtersConfig } = usePage<PageProps>().props;
    const expansionOptions = expansionFilterOptions(expansions);
    const fallbackProductTypeSlug = Object.keys(filtersConfig)[0];

    const currentFiltersConfig =
        (hidePropertyFiltersWithoutProductType && !currentProductType
            ? []
            : currentProductType
              ? filtersConfig[currentProductType.slug]
              : filtersConfig[fallbackProductTypeSlug]) ?? [];

    const handleChange = (
        property: string,
        value: string,
        preserveState = false,
    ) => {
        const updatedFilters = {
            ...filters,
            [property]: value,
        };
        onFilterChange({
            q: updatedFilters,
            expansionSlug: expansion?.slug,
            preserveState,
        });
    };

    useEffect(() => {
        if (!sticky) return;

        const sentinelElement = sentinelRef.current;
        const filtersElement = filtersRef.current;
        const siteHeader = document.querySelector<HTMLElement>(
            '[data-testid="site-header"]',
        );

        if (!siteHeader || !sentinelElement || !filtersElement) return;

        let animationFrameId = 0;

        const resetStyles = () => {
            siteHeader.style.transform = '';
            siteHeader.style.pointerEvents = '';
            filtersElement.style.top = '';
        };

        const syncLayout = () => {
            if (window.innerWidth < 1024) {
                resetStyles();
                return;
            }

            const headerHeight = siteHeader.offsetHeight;
            const sentinelTop = sentinelElement.getBoundingClientRect().top;
            const visibleHeaderHeight = Math.max(
                0,
                Math.min(headerHeight, sentinelTop),
            );
            const hiddenHeaderHeight = headerHeight - visibleHeaderHeight;

            siteHeader.style.transform = `translateY(-${hiddenHeaderHeight}px)`;
            siteHeader.style.pointerEvents =
                hiddenHeaderHeight >= headerHeight ? 'none' : '';
            filtersElement.style.top = `${visibleHeaderHeight}px`;
        };

        const requestSync = () => {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = window.requestAnimationFrame(syncLayout);
        };

        requestSync();
        window.addEventListener('scroll', requestSync, { passive: true });
        window.addEventListener('resize', requestSync);

        return () => {
            cancelAnimationFrame(animationFrameId);
            resetStyles();
            window.removeEventListener('scroll', requestSync);
            window.removeEventListener('resize', requestSync);
        };
    }, [sticky]);

    return (
        <>
            {sticky && (
                <div
                    ref={sentinelRef}
                    className="hidden lg:block"
                    aria-hidden="true"
                />
            )}
            <div
                ref={filtersRef}
                className={cn(
                    'hidden bg-white py-2.5 lg:block dark:bg-background',
                    showBorder && 'border-b',
                    sticky && 'lg:sticky lg:top-24 lg:z-[50]',
                    className,
                )}
                data-testid="desktop-filters"
            >
                <div className="container px-4 sm:px-6 lg:px-8">
                    <div
                        className="flex flex-wrap items-center gap-2.5"
                        data-testid="desktop-filters-row"
                    >
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
                            <div className="flex shrink-0 items-center text-sm font-medium">
                                <Filter className="mr-1.5 h-3.5 w-3.5" />
                                <span data-testid="desktop-filters-label">
                                    Фільтри
                                </span>
                            </div>

                            {extraFilters?.map((filter) => (
                                <div
                                    key={filter.key}
                                    className="w-[200px] flex-none xl:w-[210px]"
                                >
                                    <PropertyFilterSelect
                                        value={filters[filter.key] || 'all'}
                                        onChange={(val) =>
                                            handleChange(filter.key, val)
                                        }
                                        options={filter.options}
                                        searchable={filter.searchable}
                                        multiselect={filter.multiselect}
                                        allLabel={filter.allLabel}
                                        size="compact"
                                        testId={filter.testId}
                                    />
                                </div>
                            ))}

                            {!(
                                expansionOptions.length === 0 ||
                                currentProductType?.slug === 'accessories' ||
                                currentProductType?.slug === 'sealed'
                            ) && (
                                <div className="w-[200px] flex-none xl:w-[210px]">
                                    <PropertyFilterSelect
                                        value={expansion?.slug || 'all'}
                                        onChange={(val) =>
                                            onFilterChange({
                                                q: filters,
                                                expansionSlug: val,
                                            })
                                        }
                                        options={expansionOptions}
                                        searchable
                                        showSelectionIndicator={false}
                                        allLabel="Колекції"
                                        searchPlaceholder="Пошук колекцій..."
                                        emptyText="Колекції не знайдено"
                                        size="compact"
                                        testId="expansion-filter"
                                    />
                                </div>
                            )}

                            {currentFiltersConfig?.map((product_property) => (
                                <div
                                    key={product_property.id}
                                    className="w-[200px] flex-none xl:w-[210px]"
                                >
                                    <PropertyFilterSelect
                                        value={
                                            filters[product_property.slug] ||
                                            'all'
                                        }
                                        onChange={(val) =>
                                            handleChange(
                                                product_property.slug,
                                                val,
                                                Boolean(
                                                    product_property.multiselect,
                                                ),
                                            )
                                        }
                                        options={
                                            product_property.product_property_options ??
                                            []
                                        }
                                        allLabel={product_property.name}
                                        searchable={product_property.searchable}
                                        multiselect={
                                            product_property.multiselect
                                        }
                                        size="compact"
                                        testId={`property-filter-${product_property.slug}`}
                                    />
                                </div>
                            ))}

                            <Button
                                variant="outline"
                                className="h-9 flex-none px-3 text-sm whitespace-nowrap"
                                data-testid="desktop-filters-reset"
                                onClick={onResetFilters}
                            >
                                Очистити фільтри
                            </Button>

                            {onSortChange && (
                                <div className="ml-auto flex shrink-0 items-center">
                                    <Select
                                        value={sort || 'newest'}
                                        onValueChange={onSortChange}
                                    >
                                        <SelectTrigger
                                            className="h-9 w-[240px] text-sm [&_svg]:h-3.5 [&_svg]:w-3.5"
                                            data-testid="profile-offers-sort-trigger"
                                        >
                                            <SelectValue placeholder="Новизною" />
                                        </SelectTrigger>
                                        <SelectContent className="text-sm">
                                            <SelectItem
                                                className="py-1 text-sm"
                                                value="newest"
                                            >
                                                Новизною
                                            </SelectItem>
                                            <SelectItem
                                                className="py-1 text-sm"
                                                value="price-low"
                                            >
                                                Ціна: від низької до високої
                                            </SelectItem>
                                            <SelectItem
                                                className="py-1 text-sm"
                                                value="price-high"
                                            >
                                                Ціна: від високої до низької
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
